import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { PageSEO } from "@/components/PageSEO";
import { SEO } from "@/lib/seo";
import { Header, Footer, WhatsAppButton } from "@/components/Layout";
import {
  API, SERVICES, PROBLEMS, SOLUTIONS, METHOD_STEPS,
  TESTIMONIALS_DATA, LOCATIONS, SOCIAL_LINKS, WHATSAPP_NUMBER,
  CONTACT_EMAIL, TEAM_ITEMS
} from "@/lib/constants";
import { 
  FileCheck, Compass, BarChart3, Phone, Mail, MapPin, 
  Facebook, Instagram, Linkedin, Menu, X, Star, ArrowRight,
  CheckCircle, Users, Clock, Award, ChevronRight, MessageCircle,
  Building, Target, TrendingUp, Shield, Play, XCircle, Zap,
  Eye, Lock, AlertTriangle, Sparkles, ArrowDown, Edit, Trash2,
  Plus, Save, LogOut, Image, Download, ShoppingCart, FileSpreadsheet,
  HelpCircle, ChevronDown, CircleDollarSign, CalendarCheck, PieChart,
  FileText, Wallet, BadgePercent, Sparkle, RefreshCw, ShieldCheck, BookOpen
} from "lucide-react";
import { HomePage } from "@/pages/HomePage";
import { MetodoPage } from "@/pages/MetodoPage";
import { ServiziPage } from "@/pages/ServiziPage";
import { ChiSiamoPage } from "@/pages/ChiSiamoPage";
import { ContattiPage } from "@/pages/ContattiPage";
import { BlogPage, ArticlePage } from "@/pages/BlogPage";
import NotFoundPage from "@/pages/NotFoundPage";
import { AdminPage } from "@/pages/AdminPage";
import { PortaleDocumentiPage } from "@/pages/PortaleDocumentiPage";
import { PannelloDirezionalePage } from "@/pages/PannelloDirezionalePage";
import { ControlloGestionePage } from "@/pages/ControlloGestionePage";
import { PrivacyPolicyPage } from "@/pages/PrivacyPolicyPage";
import { CookiePolicyPage } from "@/pages/CookiePolicyPage";
import { StrumentiPage } from "@/pages/StrumentiPage";
import QmdpCondizioniPage from "@/pages/QmdpCondizioniPage";
import QmdpVenditaPage from "@/pages/QmdpVenditaPage";
import QmdpSchedaPage from "@/pages/QmdpSchedaPage";
import QmdpIncassoPage from "@/pages/QmdpIncassoPage";
import QmdpAttivataPage from "@/pages/QmdpAttivataPage";
import QmdpAbbonamentoPage from "@/pages/QmdpAbbonamentoPage";
import { ProductLandingPage } from "@/pages/ProductLandingPage";
import { DownloadPage } from "@/pages/DownloadPage";
import { PaymentPage } from "@/pages/PaymentPage";
import LibroPage from "@/pages/LibroPage";
import ForfettariPage from "@/pages/ForfettariPage";
import ForfettariOnboardingPage from "@/pages/ForfettariOnboardingPage";

// Netflix-style Service Card
const ScrollToTop = () => {
  const location = useLocation();
  const firstLoad = useRef(true);
  useEffect(() => {
    window.scrollTo(0, 0);
    // Meta Pixel: il PageView del primo caricamento lo spara consent-init.js dopo il consenso;
    // qui tracciamo SOLO le navigazioni successive della SPA (niente doppio evento iniziale).
    if (firstLoad.current) {
      firstLoad.current = false;
      return;
    }
    if (window.SCConsent && window.SCConsent.isGranted() && typeof window.fbq === 'function' && !window.fbq.__scStub) {
      try { window.fbq('track', 'PageView'); } catch (e) { /* noop */ }
    }
  }, [location.pathname]);
  return null;
};

// App
function App() {
  const location = window.location.pathname;
  const isAdmin = location.startsWith('/admin');
  const isPayment = location.startsWith('/pay/');
  const isDownload = location.includes('/download/');
  
  return (
    <HelmetProvider>
    <div className="App font-body bg-black min-h-screen">
      <BrowserRouter>
        <ScrollToTop />
        {!isAdmin && !isPayment && !isDownload && <Header />}
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/metodo" element={<MetodoPage />} />
          <Route path="/libro" element={<LibroPage />} />
          <Route path="/forfettari" element={<ForfettariPage />} />
          <Route path="/forfettari/benvenuto" element={<ForfettariOnboardingPage />} />
          <Route path="/servizi" element={<ServiziPage />} />
          <Route path="/chi-siamo" element={<ChiSiamoPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/approfondimenti" element={<BlogPage />} />
          <Route path="/approfondimenti/:slug" element={<ArticlePage />} />
          <Route path="/portale-documenti" element={<PortaleDocumentiPage />} />
          <Route path="/pannello-direzionale" element={<PannelloDirezionalePage />} />
          {/* BOZZA non pubblicata: fuori da menu e sitemap, noindex */}
          <Route path="/controllo-di-gestione-micro-piccole-imprese" element={<ControlloGestionePage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/contatti" element={<ContattiPage />} />
          <Route path="/strumenti" element={<StrumentiPage />} />
          <Route path="/strumenti/download" element={<DownloadPage />} />
          <Route path="/strumenti/download/:token" element={<DownloadPage />} />
          <Route path="/strumenti/quanto-mettere-da-parte" element={<QmdpVenditaPage />} />
          <Route path="/strumenti/quanto-mettere-da-parte/condizioni" element={<QmdpCondizioniPage />} />
          <Route path="/quanto-mettere-da-parte/scheda/:codice" element={<QmdpSchedaPage />} />
          <Route path="/quanto-mettere-da-parte/incasso/:codice" element={<QmdpIncassoPage />} />
          <Route path="/quanto-mettere-da-parte/attivata" element={<QmdpAttivataPage />} />
          <Route path="/quanto-mettere-da-parte/abbonamento/:codice" element={<QmdpAbbonamentoPage />} />
          <Route path="/strumenti/:slug" element={<ProductLandingPage />} />
          <Route path="/acquista/:slug" element={<ProductLandingPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/pay/:slug" element={<PaymentPage />} />
          <Route path="/pay/:slug/success" element={<PaymentPage />} />
          {/* 301 Redirects from old WordPress site */}
          <Route path="/contact" element={<Navigate to="/contatti" replace />} />
          <Route path="/about" element={<Navigate to="/chi-siamo" replace />} />
          <Route path="/prodotti" element={<Navigate to="/servizi" replace />} />
          <Route path="/testimonianze" element={<Navigate to="/" replace />} />
          <Route path="/negozio" element={<Navigate to="/servizi" replace />} />
          <Route path="/carrello" element={<Navigate to="/" replace />} />
          <Route path="/pagamento" element={<Navigate to="/" replace />} />
          <Route path="/mio-account" element={<Navigate to="/" replace />} />
          <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="/contabilita" element={<Navigate to="/servizi" replace />} />
          <Route path="/app" element={<Navigate to="/portale-documenti" replace />} />
          <Route path="/grazie" element={<Navigate to="/" replace />} />
          <Route path="/guidasceltacommercialista" element={<Navigate to="/" replace />} />
          <Route path="/fondo-impresa-femminile" element={<Navigate to="/" replace />} />
          <Route path="/finanziaria2026" element={<Navigate to="/" replace />} />
          <Route path="/privacy-policy-di-casalisilvia-com" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="/cookie-policy-di-casalisilvia-com" element={<Navigate to="/cookie-policy" replace />} />
          <Route path="/cookie-policy-ue" element={<Navigate to="/cookie-policy" replace />} />
          <Route path="/product/*" element={<Navigate to="/servizi" replace />} />
          <Route path="/2021/*" element={<Navigate to="/approfondimenti" replace />} />
          <Route path="/2022/*" element={<Navigate to="/approfondimenti" replace />} />
          <Route path="/2023/*" element={<Navigate to="/approfondimenti" replace />} />
          <Route path="/2024/*" element={<Navigate to="/approfondimenti" replace />} />
          {/* Catch-all: pagina 404 non indicizzabile (il server non puo' restituire lo stato 404) */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        {!isAdmin && !isPayment && !isDownload && <Footer />}
        {!isAdmin && !isPayment && !isDownload && <WhatsAppButton />}
      </BrowserRouter>
      <Toaster position="top-right" theme="dark" richColors />
    </div>
    </HelmetProvider>
  );
}

export default App;
